import { useRouter } from "@tanstack/react-router"
import { useCallback, useEffect, useState } from "react"
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from "react-joyride"

import { useCurrentUser, useTourGuide } from "@/modules/helpers/hooks/shared"

/**
 * Để highlight toàn trang (không highlight element cụ thể):
 * - Dùng target: "body" với placement: "center"
 * - Hoặc set spotlightShadow: 'none' trong styles
 * - Hoặc set spotlightClicks: true để cho phép click qua spotlight
 */

// Custom Tooltip Component
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  onSkipAll
}: TooltipRenderProps & { onSkipAll?: () => void }) => {
  return (
    <div
      {...tooltipProps}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-[320px] p-5 animate-in fade-in slide-in-from-bottom-2 duration-300 m-2 relative"
    >
      {/* Skip All Button */}
      {onSkipAll && (
        <button
          onClick={onSkipAll}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            padding: "4px 12px",
            height: "24px",
            background: "#f3f4f6",
            color: "#045664",
            border: "none",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: "normal",
            cursor: "pointer",
            transition: "background 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#e5e7eb"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f3f4f6"
          }}
        >
          Bỏ qua tất cả
        </button>
      )}

      {/* Description */}
      <div className="text-sm text-gray-900 leading-relaxed pr-6 mt-5">{step.content}</div>

      {/* Footer */}
      <div className="flex gap-2.5 pt-2">
        {index > 0 && (
          <button
            {...backProps}
            style={{
              padding: "0 20px",
              height: "32px",
              background: "#f3f4f6",
              color: "#045664",
              border: "none",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "normal",
              cursor: "pointer"
            }}
          >
            Quay lại
          </button>
        )}
        <button
          {...primaryProps}
          style={{
            marginLeft: "auto",
            padding: "0 20px",
            height: "32px",
            background: "#0891B2",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: "normal",
            cursor: "pointer"
          }}
        >
          {isLastStep ? "Tôi đã biết" : continuous ? "Tiếp theo" : "Đóng"}
        </button>
      </div>
    </div>
  )
}

export const TourGuide = () => {
  const { user } = useCurrentUser()
  const router = useRouter()
  const { tourCompleted, hasShown, setTourCompleted, setHasShown, reset } = useTourGuide()
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)

  // Define steps
  const steps: Step[] = [
    // Step 1: AI Assistant (nếu có)
    ...(user?.isActiveAI
      ? [
          {
            target: "[data-tour='ai-button']",
            content: "Hỏi bất cứ điều gì, ZZP - AI sẽ giải đáp ngay.",
            title: "Trợ lý ZZP AI",
            placement: "bottom" as const,
            disableBeacon: true
          }
        ]
      : []),

    // Step 1: Blog
    {
      target: "[data-tour='blog-button']",
      content: "Theo dõi các cập nhật mới, mẹo tối ưu kênh và thông báo chính sách trong mục Blog",
      placement: "bottom" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='notification-button']",
      content: "Nhận thông báo về chiến dịch mới và các thông tin hỗ trợ dành cho bạn.",

      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='filter-explore']",
      content: "Tùy chỉnh bộ lọc để tìm chiến dịch phù hợp nhất.",

      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='explore-tab']",
      content: "Khám phá nhiều chiến dịch mới. Chọn chiến dịch phù hợp và bắt đầu đăng ký.",

      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='affiliate-tab']",
      content: "Kho link sản phẩm với tỷ lệ hoa hồng cao dành riêng cho bạn.",

      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='manage-tab']",
      content: "Xem các chiến dịch đã được duyệt và theo dõi tình trạng hàng mẫu tại đây.",

      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='billing-tab']",
      content: "Nhận thu nhập từ chiến dịch có CAST và rút tiền an toàn qua ví thanh toán.",
      title: "Thanh toán",
      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='profile-tab']",
      content: "Quản lý hồ sơ và dữ liệu kênh cá nhân",
      title: "Cá nhân",
      placement: "top" as const,
      disableBeacon: true
    },
    {
      target: "[data-tour='authorize-showcase']",
      content:
        "Ủy quyền Trưng bày tự động chèn đường link sản phẩm khi nhãn hàng duyệt chiến dịch.",
      title: "Ủy quyền trưng bày",
      placement: "bottom" as const,
      disableBeacon: true
    }
  ]

  const handleSkipAll = useCallback(() => {
    setTourCompleted(true)
    setRun(false)
  }, [setTourCompleted])

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, type, action } = data
      if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
        setTourCompleted(true)
        setRun(false)
        return
      }

      // Update step index for normal navigation
      if (type === "step:after") {
        if (action === "next") {
          // Handle navigation to profile page
          if (index === steps.length - 2) {
            router.navigate({ to: "/profile" })
            // Chờ đến khi element authorize-showcase xuất hiện trong DOM
            const waitForElement = (selector: string, maxWait = 3000) => {
              const startTime = Date.now()
              const checkElement = () => {
                const element = document.querySelector(selector)
                if (element) {
                  setStepIndex(index + 1)
                } else if (Date.now() - startTime < maxWait) {
                  setTimeout(checkElement, 200)
                } else {
                  // Timeout: đóng tour luôn vì element không xuất hiện
                  console.warn(
                    `Tour: Element ${selector} not found after ${maxWait}ms - Closing tour`
                  )
                  setTourCompleted(true)
                  setRun(false)
                }
              }
              setTimeout(checkElement, 500)
            }
            waitForElement("[data-tour='authorize-showcase']")
          } else {
            setStepIndex(index + 1)
          }
        } else if (action === "prev") {
          setStepIndex(index - 1)
        }
      }
    },
    [setTourCompleted, router, steps.length]
  )

  // Start tour when conditions are met
  useEffect(() => {
    if (!tourCompleted && user && !hasShown) {
      setTimeout(() => {
        setRun(true)
        setHasShown(true)
      }, 1000)
    }
  }, [user, hasShown, tourCompleted, setHasShown])

  // Expose restart function
  useEffect(() => {
    ;(window as any).restartTour = () => {
      reset()
      setTimeout(() => {
        setStepIndex(0)
        setRun(true)
      }, 500)
    }

    return () => {
      delete (window as any).restartTour
    }
  }, [reset])

  // Wrapper component để truyền onSkipAll vào CustomTooltip
  const TooltipWrapper = (props: TooltipRenderProps) => (
    <CustomTooltip {...props} onSkipAll={handleSkipAll} />
  )

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showSkipButton={false}
      showProgress={false}
      disableOverlayClose
      disableCloseOnEsc={false}
      hideCloseButton={false}
      spotlightPadding={10}
      spotlightClicks={false}
      callback={handleJoyrideCallback}
      tooltipComponent={TooltipWrapper}
      styles={{
        options: {
          zIndex: 10000,
          overlayColor: "rgba(0, 0, 0, 0.7)",
          arrowColor: "transparent"
        },
        spotlight: {
          borderRadius: 100
        }
        // Để tắt spotlight hoàn toàn (highlight toàn trang), uncomment dòng dưới:
        // spotlightShadow: 'none',
      }}
    />
  )
}
